import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { noop, useSuspenseQuery } from '@tanstack/react-query'
import * as valibot from 'valibot'
import { trpc } from '@/main'
import { useTranslation } from '@/lib/locales'
import { useHydrated } from '@/hooks/use-hydrated'
import { useSessionUser } from '@/hooks/use-session'
import { ApiTutorial } from './-components/api/playground/tutorial'
import { GuidePending } from './-components/settings/account-pending'

const DEFAULT_CODE = 'curl'

// The API guide: a document, not one integration's screen. It runs on the signed-in user's sandbox client
// (docs/notes/server/api-sandbox.md), created on first visit, so it needs no client in the URL.
export const Route = createFileRoute('/_app/account/integrations/guide')({
  // The snippet language is a search param so a shared link opens in the same language.
  validateSearch: valibot.object({
    code: valibot.fallback(valibot.optional(valibot.picklist(['curl', 'js']), DEFAULT_CODE), DEFAULT_CODE),
  }),
  search: { middlewares: [stripSearchParams({ code: DEFAULT_CODE })] },
  context: () => ({
    sandboxQueryOptions: trpc.apps.sandbox.queryOptions(),
    guideProgressQueryOptions: trpc.apps.guideProgress.queryOptions(),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.sandboxQueryOptions).catch(noop)
    context.queryClient.query(context.guideProgressQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: GuidePending,
  // Docs-style columns of its own, centred on the page (account.tsx).
  staticData: { fullWidth: true },
})

function RouteComponent() {
  const { language } = useTranslation()
  const search = Route.useSearch()
  const context = Route.useRouteContext()
  const { data: state } = useSuspenseQuery(context.sandboxQueryOptions)
  const { data: progress } = useSuspenseQuery(context.guideProgressQueryOptions)
  const user = useSessionUser()
  // The origin is rendered as TEXT in every snippet; reading it on the first client render would disagree with the
  // server's '' and fail hydration (React #418), so it waits for hydration.
  const hydrated = useHydrated()
  const origin = hydrated ? window.location.origin : ''
  return (
    <ApiTutorial
      state={state}
      progress={progress}
      userId={user?.id ?? 0}
      origin={origin}
      language={language}
      code={search.code}
    />
  )
}
